'use client';

import { useState, useMemo, useEffect } from 'react';
import { Box, Typography, ButtonBase, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { getInventory } from '@/api/feedApi';
import { getAssetUrl } from '@/utils/assetUrl';
import { useAppLanguage } from '@/lang';
import { Icon } from '@/components/icons';
import {
  TEMP_FG,
  TEMP_FG_PRIMARY,
  TEMP_FG_INVERSE,
  TEMP_FG_LOW,
  TEMP_FG_LOWER,
} from '@/constants/tempColors';
import { useDarkColors } from '@/hooks/useDarkColors';
import {
  TEMP_TEXT_BOLD_M,
  TEMP_TEXT_MEDIUM_M,
  TEMP_TEXT_SEMIBOLD_L,
  TEMP_TEXT_REGULAR_XS,
} from '@/constants/tempTypo';
import { getLocalizedText } from '@/utils/getLocalizedText';
import { useTranslator } from '@/lang';
import type { ITEM_GRADE, UserItemListItemDto } from '@/shared/types/api/Api';
import GachaItemDetail from '@/components/GachaItemDetail';
import { useGachaStore } from '@/stores/gachaStore';

const GRADE_FILTERS: Array<{ label: string; value: ITEM_GRADE | 'ALL'; labelKey?: string }> = [
  { labelKey: 'profile.tab_all', label: 'ALL', value: 'ALL' },
  { label: 'N', value: 'N' as ITEM_GRADE },
  { label: 'R', value: 'R' as ITEM_GRADE },
  { label: 'S', value: 'S' as ITEM_GRADE },
  { label: 'U', value: 'U' as ITEM_GRADE },
  { label: 'L', value: 'L' as ITEM_GRADE },
];

const GRADE_BORDER: Record<string, { gradient: string; shadow?: string }> = {
  L: { gradient: 'linear-gradient(180deg, #FFE76F 0%, #8000FF 100%)', shadow: '0 0 10px rgba(128,0,255,0.5)' },
  U: { gradient: 'linear-gradient(180deg, #DDB139 0%, #462300 100%)' },
  S: { gradient: 'linear-gradient(180deg, #DADADA 0%, #5A5A5A 100%)' },
  R: { gradient: 'linear-gradient(180deg, #4777FF 0%, #0F46E4 100%)' },
  N: { gradient: 'linear-gradient(180deg, #834D10 0%, #5F390F 100%)' },
};


export default function MyItemTab({ viewMode = 'grid' }: { viewMode?: 'grid' | 'list' }) {
  const [selectedGrade, setSelectedGrade] = useState<ITEM_GRADE | 'ALL'>('ALL');
  const [selectedItem, setSelectedItem] = useState<UserItemListItemDto | null>(null);
  const [sortMode, setSortMode] = useState<'grade' | 'createdAt'>('grade');
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const { language } = useAppLanguage();
  const { t, f } = useTranslator();
  const c = useDarkColors();

  const pendingUseItemKey = useGachaStore((s) => s.pendingUseItemKey);
  const setPendingUseItemKey = useGachaStore((s) => s.setPendingUseItemKey);

  const { data: inventory, isLoading } = useQuery({
    queryKey: ['inventory', sortMode, sortMode === 'grade' ? 'desc' : 'desc'],
    queryFn: () => getInventory({ sort: sortMode, sortDirection: 'desc' }),
    enabled: isLoggedIn,
  });

  // 뽑기 결과에서 "사용하기"로 넘어온 경우 해당 아이템 자동 선택
  useEffect(() => {
    if (pendingUseItemKey && inventory?.items) {
      const match = inventory.items.find((item) => item.Item.key === pendingUseItemKey);
      if (match) {
        setSelectedItem(match);
      }
      setPendingUseItemKey(null);
    }
  }, [pendingUseItemKey, inventory?.items]);

  const filteredItems = useMemo(() => {
    if (!inventory?.items) return [];
    if (selectedGrade === 'ALL') return inventory.items;
    return inventory.items.filter((item) => item.Item.grade === selectedGrade);
  }, [inventory?.items, selectedGrade]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* 등급 필터 칩 — Figma: left-20, w-343, h-32, gap-8, top에서 탭 아래 약 20px */}
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          px: '20px',
          height: 32,
          alignItems: 'center',
          overflow: 'hidden',
          flexShrink: 0,
          mt: '20px',
        }}
      >
        {GRADE_FILTERS.map((filter) => {
          const isActive = selectedGrade === filter.value;
          return (
            <ButtonBase
              key={filter.value}
              onClick={() => setSelectedGrade(filter.value)}
              sx={{
                px: '12px',
                py: '6px',
                borderRadius: '100px',
                bgcolor: isActive ? TEMP_FG_PRIMARY : (c.isDark ? TEMP_FG : '#f0f0f0'),
                flex: 1,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'visible',
              }}
            >
              {filter.value !== 'ALL' ? (
                <Box
                  component="img"
                  src={`/assets/items/${filter.value}.png`}
                  alt={filter.label}
                  sx={{
                    height: ['S', 'U', 'L'].includes(filter.value) ? 24 : 14,
                    width: 'auto',
                    opacity: isActive ? 1 : 0.4,
                  }}
                />
              ) : (
                <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: isActive ? TEMP_FG_INVERSE : (c.isDark ? TEMP_FG_LOW : '#888888') }}>
                  {t(filter.labelKey!)}
                </Typography>
              )}
            </ButtonBase>
          );
        })}
      </Box>

      {/* 콘텐츠 영역 — Figma: w-335 centered = px-20 on 375px */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, mt: '21px', px: '20px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* 총 N개 + 등급 높은 순 — Figma: 컨테이너 내부 pl-8, pr-4 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pl: '8px', pr: '4px' }}>
            <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fg }}>
              {f(t('mypage.total_count'), [filteredItems.length])}
            </Typography>
            <Box
              onClick={() => setSortMode((prev) => prev === 'grade' ? 'createdAt' : 'grade')}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
            >
              <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fg }}>
                {sortMode === 'grade' ? t('mypage.sort_grade_desc') : t('mypage.sort_latest')}
              </Typography>
              <Icon name="icArrowDropDown" size={24} color={c.fg} />
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
              <CircularProgress size={36} sx={{ color: TEMP_FG_PRIMARY }} />
            </Box>
          ) : filteredItems.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 300,
                gap: '12px',
              }}
            >
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: c.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="icItem" size={28} color={c.fgLower} />
              </Box>
              <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fgLow }}>
                {t('mypage.no_items')}
              </Typography>
            </Box>
          ) : viewMode === 'grid' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', pb: '20px' }}>
              {filteredItems.map((userItem) => (
                <ItemCard key={userItem.id} userItem={userItem} onClick={() => setSelectedItem(userItem)} />
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', pb: '20px' }}>
              {filteredItems.map((userItem) => (
                <ItemListRow key={userItem.id} userItem={userItem} language={language} onClick={() => setSelectedItem(userItem)} />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* 아이템 상세 모달 */}
      {selectedItem && (
        <GachaItemDetail
          userItem={selectedItem}
          language={language}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </Box>
  );
}

/* ─── 아이템 카드 (Figma 기준: 102x102, #212121 bg, rounded-10) ─── */

function ItemCard({ userItem, onClick }: { userItem: UserItemListItemDto; onClick?: () => void }) {
  const item = userItem.Item;
  const grade = item.grade || 'N';
  const isL = grade === 'L';
  const c = useDarkColors();

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%', aspectRatio: '1/1',
        bgcolor: c.isDark ? TEMP_FG : '#f0f0f0',
        borderRadius: '10px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        WebkitTapHighlightColor: 'transparent',
        flexShrink: 0,
      }}
    >
      {/* L등급: 내부 광선 이펙트 */}
      {isL && (
        <Box
          component="img"
          src={c.isDark ? '/assets/items/common-effect.svg' : '/assets/items/common-effect-light.svg'}
          alt=""
          sx={{
            position: 'absolute',
            width: '144%', height: '144%',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            opacity: 0.6,
            pointerEvents: 'none',
            animation: 'cardSpin 12s linear infinite',
            '@keyframes cardSpin': { from: { transform: 'translate(-50%,-50%) rotate(0deg)' }, to: { transform: 'translate(-50%,-50%) rotate(360deg)' } },
          }}
        />
      )}

      {/* 카드 내부: CSS 그라데이션 보더 + SVG 등급 뱃지 */}
      <Box sx={{
        position: 'absolute', inset: '9%',
        borderRadius: '10px', overflow: 'hidden',
        background: GRADE_BORDER[grade]?.gradient || '#555',
        padding: '4px',
        ...(isL && { boxShadow: GRADE_BORDER.L.shadow }),
      }}>
        <Box sx={{ width: '100%', height: '100%', borderRadius: '7px', overflow: 'hidden', bgcolor: '#eae8e8' }}>
          <Box
            component="img"
            src={item.imagePath ? getAssetUrl(item.imagePath, 'thumb') : '/images/blur.png'}
            alt=""
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.src = '/images/blur.png'; }}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>

        <Box
          component="img"
          src={`/assets/items/${grade}.png`}
          alt=""
          sx={{ position: 'absolute', top: ['N', 'R'].includes(grade) ? 8 : 4, left: ['N', 'R'].includes(grade) ? 8 : 4, height: ['N', 'R'].includes(grade) ? 22 : grade === 'L' ? 50 : 42, width: 'auto', zIndex: 2, pointerEvents: 'none', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }}
        />

        {isL && !userItem.isActive && (
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 3 }}>
            <Icon name="icLock" size={32} color="rgba(255,255,255,0.8)" />
          </Box>
        )}

        {userItem.quantity > 1 && (
          <Box
            sx={{
              position: 'absolute', bottom: -0.5, right: -0.7,
              bgcolor: '#1e1e1e', borderTopLeftRadius: '8px',
              minWidth: 30, pt: '3px', pb: '4px', px: '5px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 3,
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, color: TEMP_FG_INVERSE, textAlign: 'center' }}>
              {userItem.quantity}
            </Typography>
          </Box>
        )}
      </Box>
    </ButtonBase>
  );
}

/* ─── 리스트 행 (Figma: h-102, rounded-10, #212121 bg, pl-8 pr-14 py-18, gap-16) ─── */

function ItemListRow({
  userItem,
  language,
  onClick,
}: {
  userItem: UserItemListItemDto;
  language: string;
  onClick?: () => void;
}) {
  const item = userItem.Item;
  const grade = item.grade || 'N';
  const isL = grade === 'L';
  const name = getLocalizedText(item.name, language);

  const { t, f } = useTranslator();
  const c = useDarkColors();
  const isPiece = item.itemType === 'PIECE' || item.rewardType === 'PIECE_REWARD' || (item.key || '').startsWith('piece_');
  const hasCondition = !isPiece && (item.minIntimacy > 0 || item.requireGirlfriend);
  const hasImageReward = item.RewardAsset?.assetType === 'IMAGE';
  const hasVideoReward = item.RewardAsset?.assetType === 'VIDEO';
  const hasVoice = !!item.RewardAsset;

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        height: 102,
        bgcolor: c.isDark ? TEMP_FG : '#f0f0f0',
        borderRadius: '10px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        pl: '8px',
        pr: '14px',
        py: '18px',
        WebkitTapHighlightColor: 'transparent',
        textAlign: 'left',
      }}
    >
      {/* 좌측: 이미지 (84x84) — CSS 그라데이션 보더 */}
      <Box sx={{
        position: 'relative', width: 84, height: 84, flexShrink: 0,
        borderRadius: '10px', overflow: 'hidden',
        background: GRADE_BORDER[grade]?.gradient || '#555',
        padding: '4px',
        ...(isL && { boxShadow: GRADE_BORDER.L.shadow }),
      }}>
        {isL && (
          <Box
            component="img"
            src={c.isDark ? '/assets/items/common-effect.svg' : '/assets/items/common-effect-light.svg'}
            alt=""
            sx={{
              position: 'absolute',
              width: 147, height: 147,
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              opacity: 0.6,
              pointerEvents: 'none',
              animation: 'cardSpin 12s linear infinite',
              zIndex: 0,
            }}
          />
        )}

        <Box sx={{ width: '100%', height: '100%', borderRadius: '7px', overflow: 'hidden', bgcolor: '#eae8e8', position: 'relative', zIndex: 1 }}>
          <Box
            component="img"
            src={item.imagePath ? getAssetUrl(item.imagePath, 'thumb') : '/images/blur.png'}
            alt=""
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.src = '/images/blur.png'; }}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>

        <Box
          component="img"
          src={`/assets/items/${grade}.png`}
          alt=""
          sx={{ position: 'absolute', top: ['N', 'R'].includes(grade) ? 8 : 4, left: ['N', 'R'].includes(grade) ? 8 : 4, height: ['N', 'R'].includes(grade) ? 22 : grade === 'L' ? 50 : 42, width: 'auto', zIndex: 2, pointerEvents: 'none', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }}
        />

        {isL && !userItem.isActive && (
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 3 }}>
            <Icon name="icLock" size={32} color="rgba(255,255,255,0.8)" />
          </Box>
        )}

        {userItem.quantity > 1 && (
          <Box sx={{ position: 'absolute', bottom: -0.5, right: -0.7, bgcolor: '#1e1e1e', borderTopLeftRadius: '8px', minWidth: 30, pt: '3px', pb: '4px', px: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, color: TEMP_FG_INVERSE, textAlign: 'center' }}>
              {userItem.quantity}
            </Typography>
          </Box>
        )}
      </Box>

      {/* 우측: 텍스트 정보 — Figma: flex-1, gap-12 */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* 아이템 이름 — SemiBold 15px white */}
        <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_L, color: c.fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </Typography>

        {/* 조건 + 보상 — gap-2 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {/* 조건 */}
          {hasCondition && (
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: TEMP_FG_LOWER, flexShrink: 0 }}>{t('gacha.condition')}</Typography>
              <Box sx={{ flex: 1, display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {item.minIntimacy > 0 && (
                  <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <Icon name="icHeartFilled" size={14} color={TEMP_FG_INVERSE} />
                    <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fg }}>
                      {f(t('gacha.intimacy_above_celsius'), [item.minIntimacy])}{item.requireGirlfriend ? ',' : ''}
                    </Typography>
                  </Box>
                )}
                {item.requireGirlfriend && (
                  <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fg }}>{t('gacha.girlfriend_short')}</Typography>
                )}
              </Box>
            </Box>
          )}

          {/* 보상 */}
          <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: TEMP_FG_LOWER, flexShrink: 0 }}>{t('gacha.reward')}</Typography>
            <Box sx={{ flex: 1, display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
              {isPiece ? (
                <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fg }}>{t('gacha.piece_list_desc')}</Typography>
              ) : (
                <>
                  {item.intimacyReward > 0 && (
                    <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <Icon name="icHeartFilled" size={14} color={TEMP_FG_INVERSE} />
                      <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fg }}>
                        +{item.intimacyReward}%,
                      </Typography>
                    </Box>
                  )}
                  {hasImageReward && (
                    <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <Icon name="icImage" size={14} color={TEMP_FG_INVERSE} />
                      <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fg }}>{t('gacha.image_short')}</Typography>
                    </Box>
                  )}
                  {hasVideoReward && (
                    <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <Icon name="icPlayFill" size={14} color={TEMP_FG_INVERSE} />
                      <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fg }}>{t('gacha.video_short')}</Typography>
                    </Box>
                  )}
                  {hasVoice && (
                    <Box sx={{ bgcolor: '#0d0d0d', borderRadius: '9999px', p: '4px', display: 'flex', alignItems: 'center' }}>
                      <Icon name="icVolume" size={10} color={TEMP_FG_INVERSE} />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </ButtonBase>
  );
}
